import { isoDateTime, type BaselineNumber, type Project, type SaveBaselineOptions } from '@bigint-project/core';
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactElement } from 'react';

const BASELINE_NUMBERS: readonly BaselineNumber[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

interface BaselineManagerDialogProps {
  readonly isOpen: boolean;
  readonly project: Project;
  readonly onCancel: () => void;
  /** Connect this to `useProjectStore.getState().setBaseline`. */
  readonly onSave: (options: SaveBaselineOptions) => void;
  /** Connect this to the store's undoable Baseline clear action. */
  readonly onClear: (number: BaselineNumber) => void;
}

function defaultName(number: BaselineNumber): string {
  return `Baseline ${String(number)}`;
}

function displayDate(value: string): string {
  return value.replace('T', ' ').slice(0, 16);
}

/** Domain ISODateTime is local wall-clock time, not UTC and never carries an offset. */
export function localWallClock(date: Date): ReturnType<typeof isoDateTime> {
  const part = (value: number): string => String(value).padStart(2, '0');
  return isoDateTime(
    `${String(date.getFullYear()).padStart(4, '0')}-${part(date.getMonth() + 1)}-${part(date.getDate())}`
      + `T${part(date.getHours())}:${part(date.getMinutes())}:${part(date.getSeconds())}`,
  );
}

export function BaselineManagerDialog({ isOpen, project, onCancel, onSave, onClear }: BaselineManagerDialogProps): ReactElement | null {
  const [number, setNumber] = useState<BaselineNumber>(0);
  const [name, setName] = useState(defaultName(0));
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const titleId = useId();
  const dialogRef = useRef<HTMLFormElement>(null);
  const slotZeroRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const selectedBaseline = project.baselines.find((baseline) => baseline.number === number);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setNumber(0);
      setName(project.baselines.find((baseline) => baseline.number === 0)?.name ?? defaultName(0));
      setConfirmOverwrite(false);
      setConfirmClear(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    slotZeroRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const selectSlot = (next: BaselineNumber): void => {
    setNumber(next);
    setName(project.baselines.find((baseline) => baseline.number === next)?.name ?? defaultName(next));
    setConfirmOverwrite(false);
    setConfirmClear(false);
  };

  const submitSave = (): void => {
    onSave({
      number,
      name: name.trim() || defaultName(number),
      savedAt: localWallClock(new Date()),
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (selectedBaseline !== undefined && !confirmOverwrite) {
      setConfirmOverwrite(true);
      return;
    }
    submitSave();
  };

  const handleClear = (): void => {
    if (selectedBaseline === undefined) return;
    if (!confirmClear) {
      setConfirmClear(true);
      setConfirmOverwrite(false);
      return;
    }
    onClear(number);
    setConfirmClear(false);
  };

  const trapFocus = (event: ReactKeyboardEvent<HTMLFormElement>): void => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const items = [...focusable];
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <form aria-labelledby={titleId} aria-modal="true" className="w-full max-w-xl rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900" ref={dialogRef} onKeyDown={trapFocus} onSubmit={handleSubmit} role="dialog">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id={titleId} className="text-base font-semibold">管理基準線</h2>
          <button type="button" onClick={onCancel} aria-label="關閉基準線管理" className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">關閉</button>
        </div>
        <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">選擇一個槽位儲存目前排程。已儲存的槽位需再次確認才會覆寫。</p>
        <div aria-label="基準線槽位" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {BASELINE_NUMBERS.map((slot) => {
            const baseline = project.baselines.find((item) => item.number === slot);
            const selected = slot === number;
            return <button key={slot} ref={slot === 0 ? slotZeroRef : undefined} type="button" aria-pressed={selected} onClick={() => selectSlot(slot)} className={`rounded border p-2 text-left text-sm ${selected ? 'border-blue-600 ring-1 ring-blue-600' : 'border-slate-300 dark:border-slate-600'}`}>
              <span className="font-medium">Baseline {String(slot)}</span>
              <span className="block text-xs text-slate-500">{baseline === undefined ? '尚未儲存' : '已儲存'}</span>
              {baseline === undefined ? null : <span className="block truncate text-xs">{baseline.name} · {displayDate(baseline.savedAt as string)}</span>}
            </button>;
          })}
        </div>
        <label className="mt-4 grid gap-1 text-sm">名稱
          <input value={name} onChange={(event) => { setName(event.target.value); setConfirmOverwrite(false); }} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
        </label>
        {confirmOverwrite ? <p role="alert" className="mt-3 rounded bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">Baseline {String(number)} 已儲存為「{selectedBaseline?.name}」。再次按「確認覆寫」才會取代它。</p> : null}
        {confirmClear ? <p role="alert" className="mt-3 rounded bg-red-50 p-2 text-sm text-red-900 dark:bg-red-950 dark:text-red-100">清除 Baseline {String(number)} 後，依賴此槽位的比較資料將無法使用。再次按「確認清除」繼續。</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          {selectedBaseline === undefined ? null : <button type="button" onClick={handleClear} className="mr-auto rounded border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950">{confirmClear ? '確認清除' : '清除槽位'}</button>}
          <button type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800">取消</button>
          <button type="submit" className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">{confirmOverwrite ? '確認覆寫' : '儲存基準線'}</button>
        </div>
      </form>
    </div>
  );
}
